import type { FlightInformation, FlightLeg } from '../../domain/types'
import { Banner } from '@sunwebgroup/uds-ui/banner'
import { Button } from '@sunwebgroup/uds-ui/button'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useIntl } from '@/intl'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { buildSelfServiceUrl } from '../../domain/policies/getSelfServiceUrl'
import { useBookingDetailsStore } from '../../infrastructure/useBookingDetailsStore'
import { getIsAddSpecialLuggageAvailable } from '../selectors'
import { isPastBooking } from '../selectors/flightSelectors'
import { buildCheckedLuggageDescriptions } from '../utils/flightLuggage'
import { renderExternalLink } from '../utils/renderExternalLink'
import { BookingDetailsSection } from './BookingDetailsSection'
import styles from './ExtrasSection.module.css'
import { ExtrasSidebarItem } from './ExtrasSidebarItem'
import { SidebarItem } from './SidebarItem'
import { SpecialLuggageSheet } from './SpecialLuggageSheet/SpecialLuggageSheet'

function getAllFlightLegs(flightInformation: FlightInformation[] | null): FlightLeg[] {
  return (flightInformation ?? []).flatMap(flight => [
    ...(flight.inbound ?? []),
    ...(flight.outbound ?? []),
  ])
}

function hasNonFlexibleFlightLeg(flightInformation: FlightInformation[] | null): boolean {
  return getAllFlightLegs(flightInformation).some(leg => !leg.isFlexibleFlight)
}

function hasFlexibleFlightLeg(flightInformation: FlightInformation[] | null): boolean {
  return getAllFlightLegs(flightInformation).some(leg => leg.isFlexibleFlight)
}

// Returns the shared airline seat reservation URL when every non-flexible leg has the same
// non-empty value, otherwise null (banner then shows the copy without a link).
function getMatchingAirlineSeatReservationURL(flightInformation: FlightInformation[] | null): string | null {
  const nonFlexibleLegs = getAllFlightLegs(flightInformation).filter(leg => !leg.isFlexibleFlight)
  const [firstURL, ...restURLs] = nonFlexibleLegs.map(leg => leg.airlineSeatReservationURL)

  if (!firstURL) {
    return null
  }

  return restURLs.every(url => url === firstURL) ? firstURL : null
}

function ExtraServiceEditButton({ href, descriptionForAria: sectionTitle }: { href: string, descriptionForAria: string }) {
  const { formatMessage } = useIntl()

  return (
    <Button
      as="a"
      href={href}
      variant="secondary"
      size="XS"
      className={styles.editButton}
      ariaLabel={`${formatMessage({ id: 'common_edit' })} ${sectionTitle}`}
    >
      {formatMessage({ id: 'common_edit' })}
    </Button>
  )
}

export function ExtrasSection() {
  const { formatMessage } = useIntl()
  const siteName = useConfiguration('siteName')
  const extras = useBookingDetailsStore(state => state.extras)
  const bookingNumber = useBookingDetailsStore(state => state.bookingNumber)
  const areServicesEditable = useBookingDetailsStore(state => state.areServicesEditable)
  const specialLuggageExtras = useBookingDetailsStore(state => state.specialLuggageExtras)
  const setIn = useBookingDetailsStore(state => state.setIn)
  const isSpecialLuggageCtaShown = useBookingDetailsStore(getIsAddSpecialLuggageAvailable)
  const flightInfo = useBookingDetailsStore(state => state.flightInformation)
  const isBookingInThePast = useBookingDetailsStore(isPastBooking)
  const checkedLuggageDescriptions = buildCheckedLuggageDescriptions(flightInfo, formatMessage)

  const isAddExtraLuggageBannerShown = hasNonFlexibleFlightLeg(flightInfo) && !isBookingInThePast
  const addExtraLuggageAirlineURL = isAddExtraLuggageBannerShown ? getMatchingAirlineSeatReservationURL(flightInfo) : null
  const selfServiceUrl = areServicesEditable ? buildSelfServiceUrl(siteName, bookingNumber) : undefined
  // We hardcode the serviceType to 'Luggage' for the luggage edit URL.
  // But when we decouple services from Sitecore it shouldn't be hardcoded here
  const luggageServiceEditUrl = areServicesEditable && hasFlexibleFlightLeg(flightInfo)
    ? buildSelfServiceUrl(siteName, bookingNumber, 'Luggage')
    : undefined
  const isAddMoreServicesButtonShown = Boolean(selfServiceUrl)

  const isFlightLuggageSectionShown = checkedLuggageDescriptions.length > 0 || isAddExtraLuggageBannerShown

  const addExtraLuggageBannerText = addExtraLuggageAirlineURL
    ? formatMessage(
        { id: 'booking_details_add_extra_luggage_banner_text' },
        { link: renderExternalLink(addExtraLuggageAirlineURL) },
      )
    : formatMessage({ id: 'booking_details_add_extra_luggage_banner_text_no_link' })

  if (extras.length === 0 && !isFlightLuggageSectionShown && !isSpecialLuggageCtaShown && !isAddMoreServicesButtonShown) {
    return null
  }

  const isSpecialLuggageSectionShown = specialLuggageExtras.length > 0 || isSpecialLuggageCtaShown

  return (
    <>
      <BookingDetailsSection isSidebar title={formatMessage({ id: 'common_extras' })} iconName="Suitcase">
        <SidebarItem.Container className={styles.container}>
          {extras.map((group) => {
            const serviceType = group.selfServiceServiceType?.trim()
            const editUrl = areServicesEditable && group.isEditable && serviceType
              ? buildSelfServiceUrl(siteName, bookingNumber, serviceType)
              : undefined

            return (
              <ExtrasSidebarItem key={group.label} label={group.label} iconName={group.icon}>
                <div className={styles.extraContent}>
                  <div className={styles.extraDescriptions}>
                    {group.items.map(({ description, quantity }) => {
                      const label = `${quantity} x ${description}`
                      return <Typography key={description} variant="body-m-bold-tight">{label}</Typography>
                    })}
                  </div>
                  {editUrl && <ExtraServiceEditButton href={editUrl} descriptionForAria={group.label} />}
                </div>
              </ExtrasSidebarItem>
            )
          })}
          {isFlightLuggageSectionShown && (
            <ExtrasSidebarItem
              label={formatMessage({ id: 'booking_details_flight_luggage_title' })}
              iconName="Suitcase"
            >
              <div className={styles.extraContent}>
                <div className={styles.extraDescriptions}>
                  {checkedLuggageDescriptions.map((description, index) => (
                    // eslint-disable-next-line react/no-array-index-key -- fixed outbound-then-inbound order, at most 2 items
                    <Typography key={index} variant="body-m-bold-tight">{description}</Typography>
                  ))}
                </div>
                {luggageServiceEditUrl && (
                  <ExtraServiceEditButton
                    href={luggageServiceEditUrl}
                    descriptionForAria={formatMessage({ id: 'booking_details_flight_luggage_title' })}
                  />
                )}
              </div>

              {isAddExtraLuggageBannerShown && (
                <Banner
                  color="informative"
                  size="S"
                  hasIcon
                  className={styles.luggageBanner}
                >
                  <Banner.ContentWrapper>
                    <Banner.TextsWrapper>
                      <Banner.Text>
                        {formatMessage({ id: 'booking_details_add_extra_luggage_banner_title' })}
                      </Banner.Text>
                      <Banner.SupportingText>
                        {addExtraLuggageBannerText}
                      </Banner.SupportingText>
                    </Banner.TextsWrapper>
                  </Banner.ContentWrapper>
                </Banner>
              )}
            </ExtrasSidebarItem>
          )}
          {isSpecialLuggageSectionShown && (
            <ExtrasSidebarItem
              label={formatMessage({ id: 'booking_details_special_luggage_section_title' })}
              iconName="SuitcaseLargeBadgePlus"
            >
              {specialLuggageExtras.flatMap(group => group.items).map(({ description, quantity }) => {
                const label = `${quantity} x ${description}`
                return <Typography key={description} as="div" variant="body-m-bold-tight">{label}</Typography>
              })}
              {isSpecialLuggageCtaShown && (
                <Typography
                  as="a"
                  variant="body-m-bold-tight"
                  className={styles.ctaLink}
                  onClick={() => setIn('specialLuggage.isOpen', true)}
                >
                  {formatMessage({ id: 'booking_details_special_luggage_section_cta' })}
                </Typography>
              )}
            </ExtrasSidebarItem>
          )}
          {isAddMoreServicesButtonShown && (
            <Button
              as="a"
              href={selfServiceUrl}
              variant="secondary"
              fullWidth
              className={styles.addMoreServicesButton}
            >
              <Button.Icon glyph="Plus" />
              {formatMessage({ id: 'booking_details_add_more_services_button' })}
            </Button>
          )}
        </SidebarItem.Container>
      </BookingDetailsSection>
      <SpecialLuggageSheet />
    </>
  )
}
